import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { 
  fetchUserContext,
  switchToCompany,
  getCurrentUser,
  getUserCompanies,
  getUserGames,
  getSelectedCompany,
  getSelectedGame,
  setSelectedGame,
  getUserNameById,
  getCompanyMembers,
  getGameMembers,
  subscribeToUserData,
} from '@/lib/stores/unifiedUserStore';
import { Game, Company, UserProfile } from '@/lib/stores/storeTypes';
import { platformTranslations, type TranslationKey } from '@/lib/translations/platformTranslations';

interface SimplifiedUserDataContextValue {
  // Core data
  currentUser: UserProfile | null;
  companies: Company[];
  games: Game[];
  selectedCompany: Company | null;
  selectedGame: Game | null;
  
  // State
  isLoading: boolean;
  error: string | null;
  
  // Actions
  selectCompany: (companyId: string) => void;
  selectGame: (gameId: string) => void;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getUserNameById: (userId: string) => string;
  getCompanyMembers: (companyId: string) => UserProfile[];
  getGameMembers: (gameId: string) => UserProfile[];
  getCompanyGames: (companyId: string) => Game[];
  
  // Translation function
  t: (key: TranslationKey, fallback?: string) => string;
}

const SimplifiedUserDataContext = createContext<SimplifiedUserDataContextValue | null>(null);

const AuthenticatedUserDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataVersion, setDataVersion] = useState(0);

  // Load user context data
  const loadUserData = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchUserContext();
      setDataVersion(prev => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load user data';
      
      // If user has no companies/games (profile not complete), this is expected
      // Don't treat it as an error - just log and continue
      if (message.includes('No companies available') || message.includes('No games available')) {
        setError(null); // Don't set error state
      } else {
        setError(message);
        console.error('Failed to load user context:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Load data on mount and when user changes
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = subscribeToUserData(() => {
      setDataVersion(prev => prev + 1);
    });
    return unsubscribe;
  }, []);

  // Get current data from store (reactive to dataVersion)
  const currentUser = useMemo(() => getCurrentUser(), [dataVersion]);
  const companies = useMemo(() => getUserCompanies(), [dataVersion]);
  const games = useMemo(() => getUserGames(), [dataVersion]);
  const selectedCompany = useMemo(() => getSelectedCompany(), [dataVersion]);
  const selectedGame = useMemo(() => getSelectedGame(), [dataVersion]);
  


  // Actions
  const handleSelectCompany = useCallback((companyId: string) => {
    // Start loading immediately for responsive UI
    setIsLoading(true);
    setError(null);

    // Fetch full data for the new company in the background
    switchToCompany(companyId)
      .then(() => {
        setDataVersion(prev => prev + 1);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to load company data';
        console.error('Failed to switch company:', err);
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleSelectGame = useCallback((gameId: string) => {
    setSelectedGame(gameId);
    setDataVersion(prev => prev + 1); // Trigger re-render
  }, []);

  // Utility function to get company games
  const getCompanyGames = useCallback((companyId: string): Game[] => {
    return games.filter(game => game.company_id === companyId);
  }, [games]);

  // Translation function - reads fresh user data on each call
  const t = useCallback((key: TranslationKey, fallback?: string): string => {
    const translations = platformTranslations[key];
    if (!translations) return fallback || key;
    
    // Get fresh current user to ensure we have the latest language
    const freshUser = getCurrentUser();
    const userLanguage = freshUser?.settings?.language || currentUser?.settings?.language || 'en';
    
    
    
    // Try user's language first, then 'en', then fallback, then key
    return translations[userLanguage as keyof typeof translations] || 
           translations['en' as keyof typeof translations] || 
           fallback || 
           key;
  }, [dataVersion, currentUser]); // Also depend on currentUser to ensure re-render

  const contextValue = useMemo((): SimplifiedUserDataContextValue => ({
    currentUser,
    companies,
    games,
    selectedCompany,
    selectedGame,
    isLoading,
    error,
    selectCompany: handleSelectCompany,
    selectGame: handleSelectGame,
    refreshData: loadUserData,
    getUserNameById,
    getCompanyMembers,
    getGameMembers,
    getCompanyGames,
    t,
  }), [
    currentUser,
    companies,
    games,
    selectedCompany,
    selectedGame,
    isLoading,
    error,
    handleSelectCompany,
    handleSelectGame,
    loadUserData,
    getCompanyGames,
    t
  ]);

  return (
    <SimplifiedUserDataContext.Provider value={contextValue}>
      {children}
    </SimplifiedUserDataContext.Provider>
  );
};

export const SimplifiedUserDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <SimplifiedUserDataContext.Provider value={null}>
        {children}
      </SimplifiedUserDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return <AuthenticatedUserDataProvider>{children}</AuthenticatedUserDataProvider>;
  }

  // If not authenticated, provide null context
  return (
    <SimplifiedUserDataContext.Provider value={null}>
      {children}
    </SimplifiedUserDataContext.Provider>
  );
};

export const useSimplifiedUserData = () => {
  const context = useContext(SimplifiedUserDataContext);
  
  // Return null-safe fallback during loading states
  if (!context) {
    return {
      currentUser: null,
      companies: [],
      games: [],
      selectedCompany: null,
      selectedGame: null,
      isLoading: true,
      error: null,
      selectCompany: () => {},
      selectGame: () => {},
      refreshData: () => Promise.resolve(),
      getUserNameById: () => 'Unknown User',
      getCompanyMembers: () => [],
      getGameMembers: () => [],
      getCompanyGames: () => [],
      getFolders: () => [],
      t: (key: string, fallback?: string) => fallback || key,
    };
  }
  
  return context;
};
